import {
  WSButton,
  WSElement,
  WSFlexBox,
  WSSectionToolbar,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import { IMergedRateCard } from "@wingspanhq/payments/dist/interfaces";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useQueryPayeeEngagementRateCardByPayee } from "../../../../query/rateCards/queries/useQueryPayeeEngagementRateCardByPayee";
import { useQueryRateCard } from "../../../../query/rateCards/queries/useQueryRateCard";
import { buildEngagementRateCardPath } from "../../../Payees/utils";
import {
  getCustomValueKeys,
  hasCustomRateCard
} from "../../utils/formatRateCardData";
import { RateCardViewWidgetProps } from "../../utils/types";
import { RateCardTable } from "../RateCardTable";

const MAX_ITEMS_VISIBLE = 5;

export const RateCardViewWidget: React.FC<RateCardViewWidgetProps> = ({
  rateCardId,
  payeeEngagementId,
  payeeId,
  context,
  canCustomize = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const history = useHistory();
  const { openSnackbar } = useWSSnackbar();

  // For engagement-level rate card (when viewing from engagement page)
  const queryRateCard = useQueryRateCard(rateCardId || "", {
    enabled: !!rateCardId && !payeeEngagementId
  });

  // For contractor-level merged rate card (contains both standard + custom merged)
  const queryMergedRateCardByPayee = useQueryPayeeEngagementRateCardByPayee(
    payeeId,
    payeeEngagementId,
    {
      enabled: !!payeeId && !!payeeEngagementId
    }
  );

  // Get the merged rate card data
  const mergedRateCard = queryMergedRateCardByPayee.data as
    | IMergedRateCard
    | null
    | undefined;

  // Fetch the standard (engagement) rate card separately to compare values
  // This allows us to determine which values are custom overrides
  const standardRateCardId = mergedRateCard?.engagementRateCardId;
  // Check both field names due to backend inconsistency
  const hasCustomCard = !!(
    mergedRateCard?.payerPayeeEngagementRateCardId ||
    (mergedRateCard as any)?.payeeEngagementRateCardId
  );
  const queryStandardRateCard = useQueryRateCard(standardRateCardId || "", {
    enabled: !!standardRateCardId && hasCustomCard
  });

  // Determine which query to use based on what parameters are available
  const activeQuery =
    payeeId && payeeEngagementId ? queryMergedRateCardByPayee : queryRateCard;

  const rateCardData = activeQuery.data;
  // Only include standard rate card loading if we're actually fetching it (has custom card)
  const isLoading =
    activeQuery.isLoading || (hasCustomCard && queryStandardRateCard.isLoading);

  // Don't render if no rate card exists
  if (!isLoading && !rateCardData) {
    return null;
  }

  if (!rateCardData) {
    return null;
  }

  const isCustom = hasCustomRateCard(
    rateCardData as IMergedRateCard,
    queryStandardRateCard.data
  );
  // Pass the standard rate card to compare and identify which values are custom
  const customValueKeys = isCustom
    ? getCustomValueKeys(
        rateCardData as IMergedRateCard,
        queryStandardRateCard.data
      )
    : [];
  const valueCount = Object.keys(rateCardData.values).length;
  const hasMoreThanFive = valueCount > MAX_ITEMS_VISIBLE;
  const showToggleButton = hasMoreThanFive;

  const handleCustomize = () => {
    if (rateCardData && payeeEngagementId && payeeId) {
      // Navigate to the edit rate card page
      // Use custom rate card ID if it exists, otherwise use the standard rate card ID
      const editRateCardId =
        (rateCardData as IMergedRateCard).payerPayeeEngagementRateCardId ||
        (rateCardData as any).payeeEngagementRateCardId ||
        (rateCardData as IMergedRateCard).engagementRateCardId ||
        "";
      history.push(
        buildEngagementRateCardPath(payeeId, payeeEngagementId, editRateCardId)
      );
    } else {
      // For engagement page without contractor context, show a message
      openSnackbar({
        type: "warning",
        message: "Please select a contractor to customize their rate card"
      });
    }
  };

  // Show customize/edit button only on contractor-assignment page
  // On engagement page, only show the chip (no button)
  const showCustomizeButton =
    canCustomize && context === "contractor-assignment" && !!payeeEngagementId;

  const buttonLabel = "Customize";

  return (
    <WSElement>
      <WSSectionToolbar
        title="Rate card"
        pill={{
          theme: isCustom ? "blue" : "neutral",
          text: isCustom ? "Custom" : "Standard",
          icon: false
        }}
        button={
          showCustomizeButton
            ? {
                label: buttonLabel,
                onClick: handleCustomize
              }
            : undefined
        }
        mb="XS"
      />
      <RateCardTable
        values={rateCardData.values}
        customValueKeys={customValueKeys}
        collapsed={!isExpanded}
        loading={isLoading}
      />
      {showToggleButton && (
        <WSFlexBox p="M" pl="L">
          <WSButton.Link onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? "Collapse" : "View all"}
          </WSButton.Link>
        </WSFlexBox>
      )}
    </WSElement>
  );
};
